import { THEME_MODES, type UserPreferences } from '@base/shared';
import { useTranslation } from 'react-i18next';
import {
  SegmentedChoice,
  SettingRow,
  SettingsSection,
  Switch,
} from '../components/patterns/index.ts';
import { ErrorBox } from '../components/ui.tsx';
import { useActiveTheme } from '../platform.tsx';
import { usePreferences, useSavePreferences } from '../reading.tsx';

/**
 * Leitura, nas configurações do usuário (ADR 0128, fase 6): claro, escuro ou o do aparelho,
 * letra maior e alto contraste. Vale só para quem escolhe, em todo aparelho em que entrar.
 */
export function ReadingSection() {
  const { t } = useTranslation();
  const prefs = usePreferences();
  const save = useSavePreferences();
  const theme = useActiveTheme();
  const hasDark = JSON.stringify(theme.colors.dark) !== JSON.stringify(theme.colors.light);
  if (!prefs.data) return null;
  const set = (patch: Partial<UserPreferences>) => save.mutate({ ...prefs.data!, ...patch });
  return (
    <SettingsSection title={t('account.reading.title')} description={t('account.reading.about')}>
      <SettingRow
        label={t('account.reading.mode')}
        description={hasDark ? t('account.reading.modeHint') : t('account.reading.noDark')}
        control={(id) => (
          <SegmentedChoice
            values={THEME_MODES}
            value={prefs.data!.themeMode}
            labelledBy={id}
            disabled={save.isPending}
            labelOf={(v) => t(`account.reading.modes.${v}`)}
            onChange={(themeMode) => set({ themeMode })}
          />
        )}
      />
      <SettingRow
        label={t('account.reading.largeText')}
        description={t('account.reading.largeTextHint')}
        control={(id) => (
          <Switch
            checked={prefs.data!.largeText}
            labelledBy={id}
            onChange={(largeText) => set({ largeText })}
          />
        )}
      />
      <SettingRow
        label={t('account.reading.highContrast')}
        description={t('account.reading.highContrastHint')}
        control={(id) => (
          <Switch
            checked={prefs.data!.highContrast}
            labelledBy={id}
            onChange={(highContrast) => set({ highContrast })}
          />
        )}
      />
      {save.error != null && (
        <div className="px-5 py-3">
          <ErrorBox error={save.error} />
        </div>
      )}
    </SettingsSection>
  );
}
